/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { jsx, cssMap } from '@compiled/react';
import Link from '@atlaskit/link/link';
import Banner from '@atlaskit/banner/banner';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

// Static, content-agnostic warning banner for the M2/M3 limits programme.
// The caller supplies the message (with its `{limit}` value and `<link>` chunk) and link target.

const linkStyles = cssMap({
	content: {
		color: token('color.link'),
	},
});

type Message = {
	id: string;
	defaultMessage: string;
	description: string;
};

export type Props = {
	message: Message;
	limit: number;
	link: string;
};

export const LimitsBannerM2M3 = ({ message, limit, link }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleLinkClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'preEnforcementLimitsBannerLink',
				action: 'clicked',
			}),
		);
	};

	return (
		<Banner appearance="warning" icon={<WarningIcon label="" />}>
			{formatMessage(message, {
				limit,
				link: (chunks: ReactNode) => (
					<span css={linkStyles.content}>
						<Link href={link} target="_blank" onClick={handleLinkClick}>
							{chunks}
						</Link>
					</span>
				),
			})}
		</Banner>
	);
};

export default LimitsBannerM2M3;
